import { useState } from "react";
import axios from "axios";
import { Plus, Trash2, Edit, Droplets, Syringe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { API, formatApiError } from "@/lib/shared";

const GROUPS = [
  { id: 'iv', label: 'IV Drips', icon: Droplets, hint: 'Lactated Ringers infusions · 60 min', match: (s) => ['iv_drip', 'hydration'].includes(s.category) },
  { id: 'injection', label: 'IV Push or Injection', icon: Syringe, hint: 'IM / IV push shots · 10 min', match: () => true },
];
const CATEGORY_OPTIONS = [
  { value: 'iv_drip', label: 'IV Drip' },
  { value: 'hydration', label: 'IV Hydration' },
  { value: 'injection', label: 'IV Push / Injection' },
];
const EMPTY = { name: '', duration_minutes: 60, color: '#4A80A8', description: '', price: 0, category: 'iv_drip' };

const ServiceRow = ({ s, onEdit, onDelete }) => (
  <div className="flex items-center justify-between p-3 bg-black/30 border border-border/40 rounded-lg hover:border-primary/40 transition-colors" data-testid={`service-row-${s.id}`}>
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
      <div className="min-w-0">
        <p className="font-medium truncate">{s.name}</p>
        <p className="text-xs text-muted-foreground">{s.duration_minutes} min{s.description ? ` · ${s.description}` : ''}</p>
      </div>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      <span className="text-primary font-semibold text-sm" data-testid={`service-price-${s.id}`}>${Number(s.price || 0).toFixed(0)}</span>
      <Button variant="ghost" size="icon" onClick={() => onEdit(s)} data-testid={`service-edit-${s.id}`}><Edit className="w-4 h-4" /></Button>
      <Button variant="ghost" size="icon" className="text-destructive" onClick={() => onDelete(s.id)} data-testid={`service-delete-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
    </div>
  </div>
);

export const ServiceManagement = ({ serviceTypes, onRefresh }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState(EMPTY);

  const openCreate = (category = 'iv_drip') => { setEditingService(null); setFormData({ ...EMPTY, category, duration_minutes: category === 'injection' ? 10 : 60 }); setModalOpen(true); };
  const openEdit = (s) => { setEditingService(s); setFormData({ name: s.name, duration_minutes: s.duration_minutes, color: s.color, description: s.description || '', price: s.price ?? 0, category: s.category || 'iv_drip' }); setModalOpen(true); };

  const handleSave = async () => {
    try {
      if (editingService) { await axios.put(`${API}/service-types/${editingService.id}`, formData); toast.success("Service updated"); }
      else { await axios.post(`${API}/service-types`, formData); toast.success("Service added"); }
      setModalOpen(false); onRefresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try { await axios.delete(`${API}/service-types/${id}`); toast.success("Service deleted"); onRefresh(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  let remaining = [...serviceTypes].sort((a, b) => a.name.localeCompare(b.name));
  const sections = GROUPS.map(g => { const items = remaining.filter(g.match); remaining = remaining.filter(s => !items.includes(s)); return { ...g, items }; });

  return (
    <div className="space-y-8" data-testid="services-page">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold brand-heading gold-gradient-text">Services / Cocktails</h2>
          <p className="text-xs text-muted-foreground mt-1">{serviceTypes.length} services · grouped by type</p>
        </div>
        <Button size="sm" onClick={() => openCreate()} data-testid="add-service-btn" className="bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold"><Plus className="w-4 h-4 mr-1" />Add Service</Button>
      </div>

      {sections.map(sec => (
        <section key={sec.id} className="space-y-3" data-testid={`service-group-${sec.id}`}>
          <div className="flex items-end justify-between gap-3 border-b-2 border-primary/40 pb-2">
            <div className="flex items-center gap-2">
              <sec.icon className="w-5 h-5 text-primary" />
              <h3 className="text-base md:text-lg font-extrabold uppercase tracking-wide text-primary">{sec.label}</h3>
              <span className="text-[11px] rounded-full bg-primary/15 text-primary px-2 py-0.5 font-bold" data-testid={`service-group-count-${sec.id}`}>{sec.items.length}</span>
            </div>
            <div className="flex items-center gap-3">
              <p className="text-[11px] text-muted-foreground hidden sm:block">{sec.hint}</p>
              <Button size="sm" variant="outline" className="h-7 text-xs border-primary/40 text-primary" onClick={() => openCreate(sec.id === 'iv' ? 'iv_drip' : 'injection')} data-testid={`add-service-${sec.id}`}><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>
            </div>
          </div>
          {sec.items.length === 0 && <p className="text-xs text-muted-foreground py-2">Nothing here yet.</p>}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {sec.items.map(s => <ServiceRow key={s.id} s={s} onEdit={openEdit} onDelete={handleDelete} />)}
          </div>
        </section>
      ))}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent data-testid="service-dialog">
          <DialogHeader><DialogTitle>{editingService ? "Edit Service" : "Add Service"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Service Name *</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., IV Hydration" data-testid="service-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Type</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v, duration_minutes: v === 'injection' ? 10 : 60 })}>
                  <SelectTrigger data-testid="service-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value} data-testid={`service-category-${o.value}`}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Price ($)</Label><Input type="number" min="0" step="1" value={formData.price} onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })} data-testid="service-price" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Duration (minutes)</Label><Input type="number" value={formData.duration_minutes} onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 60 })} data-testid="service-duration" /></div>
              <div className="space-y-2"><Label>Color</Label><Input type="color" value={formData.color} onChange={(e) => setFormData({ ...formData, color: e.target.value })} className="h-10 w-20" /></div>
            </div>
            <div className="space-y-2"><Label>Description</Label><Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} data-testid="save-service-btn">{editingService ? "Update" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== USER MANAGEMENT (Admin) ====================
